import { Button, Col, Form, Row, Skeleton, Card, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { FaRegWindowClose } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import IntlMessages from 'src/domain/utility/IntlMessages';
import { productRejectionReasonsService } from 'src/shared/services/product-rejection-reasons';
import { ProductRejectionReasonsDto } from 'src/shared/services/product-rejection-reasons/dto/product-rejection-reasons-dto';
import MainUtils from 'src/shared/utils/main';
import { Formik } from 'formik';
import { CreateOrUpdateReasonsDto } from 'src/shared/services/product-rejection-reasons/dto/create-or-update-reasons-dto';
import { useBreadcrumbContext } from 'src/domain/utility/AppContextProvider/BreadcrumbContextProvider';
import RejectionReasonsInfo from './rejection-reasons-info';
import { rejectionReasonsSchema } from './schema';
import { useIntl } from 'react-intl';
import RejectionReasonsOptions from './rejection-reasons-option';
import { BsFillArrowLeftCircleFill } from 'react-icons/bs';
import { getLocaleValue } from 'src/shared/functions/change-locales';
import LanguageCode from 'src/shared/services/types/language-code';

interface ICreateOrUpdateRejectionReasonsProps { }

const CreateOrUpdateRejectionReasons: React.FC<
  ICreateOrUpdateRejectionReasonsProps
> = () => {
  const { setBreadcrumb }: any = useBreadcrumbContext();
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [rejectionReasonDetails, setRejectionReasonDetails] =
    useState<ProductRejectionReasonsDto>();
  const [isReasonDetailsLoading, setIsReasonDetailsLoading] = useState(false);

  const [initialValuesForm, setInitialValuesForm] =
    useState<CreateOrUpdateReasonsDto>();

  const [saveAndContinue, setSaveAndContinue] = useState(false);

  const [isRejectionReasonsRefresh, setIsRejectionReasonsRefresh] =
    useState(false);

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 16 },
  };

  useEffect(() => {
    setBreadcrumb([
      {
        text: <IntlMessages id='Catalog.sideBarName' />,
        url: '/catalog/products',
      },
      {
        text: <IntlMessages id='catalog.productRejectionReasons' />,
        url: '/catalog/product-rejection-reasons',
      },
      MainUtils.isEmptyValue(id)
        ? {
          text: <IntlMessages id='common.create' />,
          url: '/catalog/product-rejection-reasons/create',
        }
        : {
          text: <IntlMessages id='common.edit' />,
          url: `/catalog/product-rejection-reasons/edit/${id}`,
        },
    ]);
  }, [id]);

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          setIsReasonDetailsLoading(true);
          const details =
            await productRejectionReasonsService.getRejectionReasonById(id);
          setRejectionReasonDetails(details);
        } catch (error) {
          console.error(error);
        } finally {
          setIsReasonDetailsLoading(false);
        }
      })();
    }
  }, [id, isRejectionReasonsRefresh]);

  useEffect(() => {
    if (rejectionReasonDetails)
      setInitialValuesForm({
        name: rejectionReasonDetails?.Name,
        isActive: rejectionReasonDetails?.IsActive,
        locales: rejectionReasonDetails?.Locales?.map((item) => {
          return {
            id: item?.Id,
            languageId: item?.LanguageId,
            localeKey: item?.LocaleKey,
            localeValue: item?.LocaleValue,
          };
        }),
      });
  }, [form, rejectionReasonDetails]);

  const itemsTab: TabsProps['items'] = !id
    ? [
      {
        key: 'reasonInfo',
        label: `${messages[
          'productRejectionReasons.create.titleTab'
        ].toString()}`,
        children: <RejectionReasonsInfo />,
      },
    ]
    : [
      {
        key: 'reasonInfo',
        label: `${messages[
          'productRejectionReasons.create.titleTab'
        ].toString()}`,
        children: <RejectionReasonsInfo />,
      },
      {
        key: 'options',
        label: `${messages[
          'specificationAttributes.specificationAttributesInfo.options'
        ].toString()}`,
        children: <RejectionReasonsOptions />,
      },
    ];

  const handleSubmit = async (values: any, setSubmitting: any) => {
    try {
      setSubmitting(true);
      let responseRejectionReason: any;
      if (MainUtils.isEmptyValue(id))
        responseRejectionReason =
          await productRejectionReasonsService.addNewRejectionReason({
            ...values,
            name: !MainUtils.isEmptyValue(
              getLocaleValue(values?.locales, LanguageCode.English, 'name'),
            )
              ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
              : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
          });
      else
        responseRejectionReason =
          await productRejectionReasonsService.updateRejectionReason(id ?? '', {
            ...values,
            name: !MainUtils.isEmptyValue(
              getLocaleValue(values?.locales, LanguageCode.English, 'name'),
            )
              ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
              : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
          });
      if (saveAndContinue)
        MainUtils.isEmptyValue(id)
          ? navigate(
            `/catalog/product-rejection-reasons/edit/${responseRejectionReason.Id}`,
          )
          : setIsRejectionReasonsRefresh(!isRejectionReasonsRefresh);
      else navigate('/catalog/product-rejection-reasons');
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Card>
      <Formik
        enableReinitialize
        initialValues={
          initialValuesForm
            ? initialValuesForm
            : {
              name: '',
              description: '',
              isActive: false,
              locales: [],
            }
        }
        onSubmit={(values, { setSubmitting }: any) =>
          handleSubmit(values, setSubmitting)
        }
        validationSchema={rejectionReasonsSchema}
        validateOnChange={false}
        validateOnBlur={false}>
        {({ handleSubmit, isSubmitting }: any) => (
          <Form>
            <Skeleton active loading={isReasonDetailsLoading}>
              <Row gutter={[0, 48]}>
                <Col span={24}>
                  <Row
                    gutter={[0, 20]}
                    justify='space-between'
                    className='fullContent'>
                    <Col lg={16} sm={24}>
                      <div className='text-title'>
                        <span className='icon-title'>
                          <FaRegWindowClose />
                        </span>
                        {MainUtils.isEmptyValue(id) ? (
                          <IntlMessages id='productRejectionReasons.create.title' />
                        ) : (
                          <IntlMessages id='productRejectionReasons.edit.title' />
                        )}
                        <small className='back-to-list'>
                          <BsFillArrowLeftCircleFill className='back-to-icon' />
                          <a
                            onClick={() => {
                              navigate('/catalog/product-rejection-reasons');
                            }}>
                            <IntlMessages id='productRejectionReasons.backToProductRejectionReasonstList' />
                          </a>
                        </small>
                      </div>
                    </Col>

                    <Col>
                      <Row gutter={[10, 5]}>
                        <Col>
                          <Button
                            type='primary'
                            size='small'
                            onClick={() => {
                              setSaveAndContinue(true);
                              handleSubmit();
                            }}
                            loading={saveAndContinue ? isSubmitting : false}
                            className='add-button'>
                            <IntlMessages id='common.saveAndContinueEdit' />
                          </Button>
                        </Col>
                        <Col>
                          <Button
                            type='primary'
                            size='small'
                            onClick={() => {
                              setSaveAndContinue(false);
                              handleSubmit();
                            }}
                            loading={saveAndContinue ? false : isSubmitting}
                            className='add-button'>
                            <IntlMessages id='common.save' />
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>

                <Col span={24}>
                  <Tabs defaultActiveKey='reasonInfo' items={itemsTab} />
                </Col>
              </Row>
            </Skeleton>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export default CreateOrUpdateRejectionReasons;
